import Phaser from "phaser";
import { Player, type Origin } from "../entities/Player";
import { worldToIso, isoDepth, TILE_W, TILE_H } from "../core/iso";
import type { InteractableDef } from "../core/types";
import * as P from "../art/props";
import { worldSign } from "../art/safety";
import { DISTRICTS } from "../data/city";
import type { ChapterData } from "../data/content-types";
import { itemName } from "../data/items";
import { GameState } from "../systems/GameState";
import { InventoryManager } from "../systems/InventoryManager";
import { EquipmentManager } from "../systems/EquipmentManager";
import { HazardManager } from "../systems/HazardManager";
import { MissionManager } from "../systems/MissionManager";
import { SecuritySystem } from "../systems/SecuritySystem";
import { UI, type AssessResult } from "../ui/UI";
import { Sfx } from "../systems/Sfx";
import { SaveManager } from "../core/SaveManager";
import { bus } from "../core/EventBus";

interface SceneInteractable {
  def: InteractableDef;
  root: Phaser.GameObjects.Container;
}

/**
 * Escena base genérica de capítulo (data-driven). Contiene todo el motor
 * jugable común: caminar, colisiones, depth-sort, interacción por [E],
 * investigación, diálogo, taquilla, paso extra (LOTO), trabajar/resolver
 * y salir a la ciudad. Cada capítulo es una subclase fina que aporta sus
 * DATOS y su decorado.
 */
export abstract class WalkChapter extends Phaser.Scene {
  protected abstract chapter: ChapterData;
  protected abstract grid: { w: number; h: number };
  /** Punto de aparición por defecto (si no hay guardado). */
  protected spawn: { x: number; y: number } = { x: 6, y: 7 };

  protected origin: Origin = { ox: 0, oy: 0 };
  protected player!: Player;
  private items: SceneInteractable[] = [];
  private nearest: SceneInteractable | null = null;
  private floor?: Phaser.GameObjects.Graphics;

  private keyE!: Phaser.Input.Keyboard.Key;
  private keyUp!: Phaser.Input.Keyboard.Key;
  private keyDown!: Phaser.Input.Keyboard.Key;
  private keyLeft!: Phaser.Input.Keyboard.Key;
  private keyRight!: Phaser.Input.Keyboard.Key;
  private prompt!: Phaser.GameObjects.Container;
  private promptText!: Phaser.GameObjects.Text;
  private promptBg!: Phaser.GameObjects.Graphics;
  private promptKey!: Phaser.GameObjects.Text;
  private highlight!: Phaser.GameObjects.Container;
  private hlPhase = 0;
  private idleT = 0;
  private footT = 0;
  private footHi = false;
  private guideArrow?: Phaser.GameObjects.Container;
  /** Luces parpadeantes (LEDs de máquinas): se animan en update y se recolocan al resize. */
  private ambient: { obj: Phaser.GameObjects.Arc; wx: number; wy: number; dx: number; dy: number; sp: number; ph: number }[] = [];
  /** Efectos ambientales (balanceo, pulso, partículas ascendentes). Autolimpiante por .active. */
  private fx: {
    obj: Phaser.GameObjects.GameObject & { x: number; y: number; setAngle?(a: number): unknown; setAlpha?(a: number): unknown; setScale?(a: number): unknown };
    kind: "sway" | "pulse" | "rise"; sp: number; ph: number; amp: number; base: number; by?: number; rise?: number; grow?: number;
  }[] = [];

  protected state!: GameState;
  protected inv!: InventoryManager;
  protected equip!: EquipmentManager;
  protected hazard!: HazardManager;
  protected mission!: MissionManager;
  protected security!: SecuritySystem;
  protected ui!: UI;

  private modalOpen = false;
  private saveAccum = 0;
  /** Objetivos ya anunciados por toast (para no repetir en cada recarga). */
  private notifiedObjectives = new Set<string>();
  private notifiedReady = false;

  /** Si true, no se pintan etiquetas de texto permanentes sobre los props
   *  (se descubre con el prompt [E] + el indicador). Por defecto, sí. */
  protected hideLabels = true;

  /** Paredes traseras: dan sensación de interior cerrado (naves, oficinas, salas).
   *  Se dibujan en las dos aristas del fondo del recinto, tras los props. */
  protected buildWalls(opts: { left: number; right: number; height?: number; base?: number; texture?: "metal" | "tile" | "plain" }): void {
    const h = opts.height ?? 150;
    const g = this.add.graphics().setDepth(-12000);
    const gw = this.grid.w, gh = this.grid.h;
    const A = this.iso(-0.5, -0.5);
    const B = this.iso(gw - 0.5, -0.5);
    const D = this.iso(-0.5, gh - 0.5);
    const up = (p: { sx: number; sy: number }) => ({ sx: p.sx, sy: p.sy - h });
    const A2 = up(A), B2 = up(B), D2 = up(D);
    const quad = (p: { sx: number; sy: number }[], c: number, a = 1): void => {
      g.fillStyle(c, a); g.beginPath(); g.moveTo(p[0].sx, p[0].sy);
      for (let i = 1; i < p.length; i++) g.lineTo(p[i].sx, p[i].sy);
      g.closePath(); g.fillPath();
    };
    quad([A, B, B2, A2], opts.right);          // pared trasera-derecha (cara SE, más clara)
    quad([A, D, D2, A2], opts.left);           // pared trasera-izquierda (cara SW, más oscura)
    // textura sobre las caras (líneas siguiendo la perspectiva)
    if (opts.texture === "metal" || opts.texture === "tile") {
      const step = opts.texture === "metal" ? 0.5 : 1;
      g.lineStyle(1, 0x000000, 0.08);
      for (let x = 0; x <= gw; x += step) { const b = this.iso(x - 0.5, -0.5); g.beginPath(); g.moveTo(b.sx, b.sy); g.lineTo(b.sx, b.sy - h); g.strokePath(); }
      for (let y = 0; y <= gh; y += step) { const b = this.iso(-0.5, y - 0.5); g.beginPath(); g.moveTo(b.sx, b.sy); g.lineTo(b.sx, b.sy - h); g.strokePath(); }
      if (opts.texture === "tile") {
        for (const frac of [0.4, 0.7]) { const yy = -h * frac; const l = { sx: A.sx, sy: A.sy + yy }, r = { sx: B.sx, sy: B.sy + yy }, dd = { sx: D.sx, sy: D.sy + yy }; g.lineStyle(1, 0x000000, 0.06); g.beginPath(); g.moveTo(l.sx, l.sy); g.lineTo(r.sx, r.sy); g.moveTo(l.sx, l.sy); g.lineTo(dd.sx, dd.sy); g.strokePath(); }
      }
    }
    // rodapié
    if (opts.base != null) {
      quad([A, B, { sx: B.sx, sy: B.sy - 12 }, { sx: A.sx, sy: A.sy - 12 }], opts.base);
      quad([A, D, { sx: D.sx, sy: D.sy - 12 }, { sx: A.sx, sy: A.sy - 12 }], opts.base);
    }
    // remate superior + esquina interior
    g.lineStyle(2, 0x000000, 0.14);
    g.beginPath(); g.moveTo(A2.sx, A2.sy); g.lineTo(B2.sx, B2.sy); g.moveTo(A2.sx, A2.sy); g.lineTo(D2.sx, D2.sy); g.strokePath();
    g.lineStyle(2, 0x000000, 0.10);
    g.beginPath(); g.moveTo(A.sx, A.sy); g.lineTo(A2.sx, A2.sy); g.strokePath();
  }

  /** Fondo de la escena (cielo/calima/color de cámara). Por defecto, calima neutra
   *  (evita el vacío oscuro en los capítulos que no lo sobrescriben). */
  protected buildBackdrop(): void {
    const cam = this.cameras.main;
    cam.setBackgroundColor(0x8f8a7c);
    const over = 420;
    const sky = this.add.graphics().setScrollFactor(0).setDepth(-19000);
    sky.fillGradientStyle(0xb7b1a2, 0xb7b1a2, 0x8f8a7c, 0x8f8a7c, 1);
    sky.fillRect(-over, -over, this.scale.width + over * 2, this.scale.height * 0.6 + over);
  }
  /** El decorado ambiental propio de cada capítulo (sobre el suelo). */
  protected buildDecor(): void {}

  /** Señalización PRL real colocada en el escenario (postes/paredes). Cada
   *  capítulo la declara; se descubre observando, sin inspeccionar. */
  protected worldSigns: { id: string; x: number; y: number; wall?: boolean }[] = [];
  private signObjs: Phaser.GameObjects.GameObject[] = [];
  protected buildSigns(): void {
    this.signObjs.forEach((o) => o.destroy());
    this.signObjs = [];
    for (const w of this.worldSigns) {
      const c = worldSign(this, w.id, { wall: w.wall });
      const p = this.iso(w.x, w.y);
      c.setPosition(p.sx, p.sy).setDepth(isoDepth(w.x, w.y));
      this.signObjs.push(c);
    }
  }
  /** Cambios visuales al resolver (barandilla, resguardo repuesto...). */
  protected onResolved(): void {}
  /** Cámara: sigue a EPI con zoom y límites del recinto. Los capítulos con
   *  cámara propia lo sobrescriben. */
  protected setupCamera(): void {
    const cam = this.cameras.main;
    const pts = [this.iso(0, 0), this.iso(this.grid.w - 1, 0), this.iso(0, this.grid.h - 1), this.iso(this.grid.w - 1, this.grid.h - 1)];
    const xs = pts.map((p) => p.sx), ys = pts.map((p) => p.sy);
    const minX = Math.min(...xs) - TILE_W, maxX = Math.max(...xs) + TILE_W;
    const minY = Math.min(...ys) - 280, maxY = Math.max(...ys) + TILE_H * 3;
    cam.setBounds(minX, minY, maxX - minX, maxY - minY);
    cam.setZoom(1.2);
    cam.startFollow(this.player.container, true, 0.12, 0.12);
    cam.setDeadzone(160, 110);
  }
  /** Precarga de assets (sprites). Por defecto no hay assets que cargar. */
  protected preloadAssets(): void {}

  /**
   * FUENTE de interactivos de la escena. Por defecto, los datos del capítulo.
   * Las escenas montadas desde un editor (Tiled) la sobrescriben para tomar
   * las POSICIONES del mapa, conservando la LÓGICA por `id`/`ref`.
   */
  protected getInteractables(): InteractableDef[] {
    return this.chapter.interactables;
  }

  /**
   * ¿La celda de mundo (x,y) está bloqueada por una colisión sólida?
   * Por defecto no hay sólidos (solo el borde de la rejilla). Las escenas con
   * capa COLLISION lo sobrescriben.
   */
  protected isBlocked(_x: number, _y: number): boolean {
    return false;
  }

  /** Hook de Phaser: precarga antes de create(). */
  preload(): void {
    this.preloadAssets();
  }

  protected get loc(): string {
    return this.chapter.location;
  }

  create(): void {
    this.state = new GameState();
    this.inv = new InventoryManager(this.state);
    this.equip = new EquipmentManager(this.state);
    this.hazard = new HazardManager(this.state, this.loc);
    this.mission = new MissionManager(this.state, this.chapter);
    this.security = new SecuritySystem(this.state);
    this.ui = new UI(this.state, this.inv, this.equip);

    this.security.init(this.loc, this.chapter.secStart);
    this.mission.start();
    // marca como "ya anunciado" lo que estuviera cumplido al entrar (evita spam al recargar)
    for (const v of this.mission.visibleObjectives()) if (v.done) this.notifiedObjectives.add(v.objective.id);
    this.notifiedReady = this.mission.canReport();

    this.computeOrigin();
    this.buildBackdrop();
    this.drawFloor();
    this.buildDecor();
    this.buildSigns();
    this.spawnInteractables();
    this.spawnPlayer();
    this.setupInput();
    this.buildPrompt();
    this.buildGuide();
    this.setupCamera();
    this.refreshEquipped();
    this.player.applyEquipment(this.equip.slots());
    this.updateHud();
    this.cameras.main.fadeIn(340, 20, 16, 10); // entrada con fundido

    // barra de controles (como en la ciudad) — ayuda constante y discreta
    this.add
      .text(16, 12, "Mover: WASD/flechas · Actuar: E", {
        fontFamily: "Segoe UI, sans-serif", fontSize: "14px", color: "#f4ead0",
        backgroundColor: "#00000055", padding: { x: 8, y: 5 },
      })
      .setScrollFactor(0)
      .setDepth(100000);

    // aviso de cómo se juega LA PRIMERA VEZ que se entra a un caso (una sola vez)
    if (!this.state.hasFlag("tutorial_seen")) {
      this.state.setFlag("tutorial_seen");
      this.time.delayedCall(650, () => this.ui.toast("Cómo se juega", "Muévete con WASD y acércate a lo que resalte", "info"));
      this.time.delayedCall(2650, () => this.ui.toast("Pulsa E", "Para hablar, inspeccionar o actuar sobre lo cercano", "info"));
      this.time.delayedCall(4650, () => this.ui.toast("Objetivo", "Habla, detecta los riesgos y arréglalos con seguridad", "info"));
    }

    const offEquip = bus.on("EQUIPMENT_CHANGED", () => this.player.applyEquipment(this.equip.slots()));
    this.events.once("shutdown", () => {
      offEquip();
      this.ui.destroy();
    });

    this.scale.on("resize", () => {
      this.computeOrigin();
      this.drawFloor();
      this.buildDecor();
      this.buildSigns();
      this.repositionStatics();
    });
  }

  protected computeOrigin(): void {
    this.origin = { ox: this.scale.width / 2 - (this.grid.w - this.grid.h) * (TILE_W / 4), oy: 130 };
  }

  protected iso(x: number, y: number): { sx: number; sy: number } {
    const p = worldToIso(x, y);
    return { sx: this.origin.ox + p.sx, sy: this.origin.oy + p.sy };
  }

  /** Tipo de superficie por tile. Por defecto, hormigón industrial con parches
   *  de tierra. Cada capítulo puede sobrescribirlo para su ambiente. */
  protected floorKind(x: number, y: number): P.TileKind {
    const h = (x * 928371 + y * 12347) & 255;
    return h % 11 === 0 ? "dirt" : "concrete";
  }

  protected drawFloor(): void {
    this.floor?.destroy();
    const g = this.add.graphics().setDepth(-10000);
    for (let x = 0; x < this.grid.w; x++) {
      for (let y = 0; y < this.grid.h; y++) {
        const { sx, sy } = this.iso(x, y);
        P.paintTile(g, sx, sy, this.floorKind(x, y), x, y);
      }
    }
    this.floor = g;
  }

  private spawnInteractables(): void {
    this.items.forEach((i) => i.root.destroy());
    this.items = [];
    for (const def of this.getInteractables()) {
      const root = this.buildMarker(def);
      const { sx, sy } = this.iso(def.pos.x, def.pos.y);
      root.setPosition(sx, sy).setDepth(isoDepth(def.pos.x, def.pos.y));
      this.items.push({ def, root });
    }
    // LED de alerta parpadeante sobre cada máquina/cuadro (afordancia + vida)
    this.ambient.forEach((a) => a.obj.destroy());
    this.ambient = [];
    for (const it of this.items) {
      if (it.def.type !== "machine") continue;
      this.addBlink(it.def.pos.x, it.def.pos.y, 10, -44, 2.6, 0xff3b30, 0.009); // rojo: alerta parpadeante
    }
    // el estado "atendido" va DESPUÉS de crear los LEDs (para poder ponerlos en verde)
    this.applyPersistedAttended();
  }

  /** Registra un LED parpadeante en coordenadas de mundo (dx/dy = offset en px). */
  protected addBlink(wx: number, wy: number, dx: number, dy: number, r: number, color: number, sp = 0.007): void {
    const p = this.iso(wx, wy);
    const c = this.add.circle(p.sx + dx, p.sy + dy, r, color).setDepth(55000);
    this.ambient.push({ obj: c, wx, wy, dx, dy, sp, ph: wx * 0.7 + wy });
  }

  /** Balanceo suave de una lámpara colgante. */
  protected addSway(obj: Phaser.GameObjects.Container, amp = 1.3, sp = 0.0016): void {
    this.fx.push({ obj, kind: "sway", amp, sp, ph: obj.x * 0.02, base: 0 });
  }

  /** Pulso (respiración) de alpha de un foco/objeto luminoso. */
  protected addPulse(obj: Phaser.GameObjects.Graphics, amp = 0.12, sp = 0.0045, base = 0.9): void {
    this.fx.push({ obj, kind: "pulse", amp, sp, ph: obj.x * 0.03, base });
  }

  /** Partículas que ascienden y se desvanecen (chispas, vapor, humo) en un punto de mundo.
   *  Devuelve las partículas creadas (para poder apagarlas/destruirlas después). */
  protected addRisers(wx: number, wy: number, opts: { count?: number; color: number; alpha: number; r: number; rise: number; sp: number; grow?: number; dx?: number; dyBase?: number; depth?: number }): Phaser.GameObjects.Arc[] {
    const p = this.iso(wx, wy);
    const n = opts.count ?? 4;
    const cy = p.sy + (opts.dyBase ?? 0);
    const made: Phaser.GameObjects.Arc[] = [];
    for (let i = 0; i < n; i++) {
      const c = this.add.circle(p.sx + (opts.dx ?? 0), cy, opts.r, opts.color, opts.alpha).setDepth(opts.depth ?? 40000);
      this.fx.push({ obj: c, kind: "rise", sp: opts.sp, ph: i / n, amp: opts.alpha, base: 0, by: cy, rise: opts.rise, grow: opts.grow ?? 1.4 });
      made.push(c);
    }
    return made;
  }

  /** Efecto en el mundo al ATENDER un hotspot (apagar fuego, limpiar mancha,
   *  llamar a emergencias...). `live` = ocurre ahora (anima); si no, es reconstrucción. */
  protected attendedEffect(_ref: string, _live: boolean): void {}

  /** Contenedor visual de un interactivo por su ref (para efectos de escena). */
  protected itemRoot(ref: string): Phaser.GameObjects.Container | undefined {
    return this.items.find((i) => i.def.ref === ref)?.root;
  }

  /** Sale a otra escena con un fundido a negro (transición suave, no corte seco). */
  private leaveTo(key: string): void {
    const cam = this.cameras.main;
    cam.fadeOut(280, 20, 16, 10);
    cam.once("camerafadeoutcomplete", () => this.scene.start(key));
  }

  /** Flecha que guía hacia el siguiente sitio relevante a investigar. */
  private buildGuide(): void {
    const g = this.add.graphics();
    g.fillStyle(0x1a1206, 0.2); g.fillEllipse(0, 2, 20, 6);
    g.fillStyle(0xf0a91b, 1); g.lineStyle(2, 0x241d14, 1);
    g.beginPath(); g.moveTo(11, 0); g.lineTo(-6, -9); g.lineTo(-1, 0); g.lineTo(-6, 9); g.closePath();
    g.fillPath(); g.strokePath();
    this.guideArrow = this.add.container(0, 0, [g]).setDepth(92000).setVisible(false);
  }

  /** Activa/desactiva el modo ayuda (flecha guía). Se guarda en el guardado. */
  private toggleAyuda(): void {
    const s = SaveManager.get();
    const on = !s.flags?.ayuda;
    SaveManager.patch({ flags: { ...s.flags, ayuda: on } });
    this.ui.toast("Modo ayuda", on ? "Flecha guía activada (G)" : "Flecha guía desactivada (G)", on ? "good" : "info");
    if (!on) this.guideArrow?.setVisible(false);
  }

  private updateGuide(): void {
    if (!this.guideArrow) return;
    // la flecha guía SOLO se muestra con el "modo ayuda" activado (opcional, por defecto OFF)
    if (this.modalOpen || !SaveManager.get().flags?.ayuda) { this.guideArrow.setVisible(false); return; }
    const px = this.player.x, py = this.player.y;
    const canReport = this.mission.canReport();
    let best: Phaser.GameObjects.Container | null = null;
    let bestD = Infinity;
    for (const it of this.items) {
      const d = it.def;
      if (d.type === "door" || d.type === "locker") continue;
      if (this.isAttended(d)) continue;
      if (canReport && d.id !== this.chapter.workId) continue; // si ya puede rematar, guía SOLO al trabajo
      const dist = Math.hypot(d.pos.x - px, d.pos.y - py);
      if (dist < bestD) { bestD = dist; best = it.root; }
    }
    if (!best || bestD < 1.9) { this.guideArrow.setVisible(false); return; }
    const pc = this.player.container;
    const bob = Math.sin(this.idleT * 0.006) * 4;
    const ang = Math.atan2(best.y - pc.y, best.x - pc.x);
    this.guideArrow.setPosition(pc.x + Math.cos(ang) * 42, pc.y - 58 + bob + Math.sin(ang) * 20).setRotation(ang).setVisible(true);
  }

  /** Feedback al equiparse: pequeño pop del personaje + chispas + sonido. */
  private equipPop(): void {
    Sfx.good();
    const p = this.player.container;
    this.tweens.add({ targets: p, scaleX: 1.14, scaleY: 1.14, yoyo: true, duration: 130, ease: "Quad.easeOut" });
    for (let i = 0; i < 6; i++) {
      const s = this.add.circle(p.x + Phaser.Math.Between(-10, 10), p.y - 20, 2.4, 0xfff2a0, 0.95).setDepth(94000);
      this.tweens.add({ targets: s, y: s.y - Phaser.Math.Between(20, 42), alpha: 0, duration: 500, ease: "Quad.easeOut", onComplete: () => s.destroy() });
    }
  }

  /** Celebración al resolver un caso: destello de cámara + confeti que sube. */
  private celebrate(): void {
    this.cameras.main.flash(420, 150, 210, 170);
    const p = this.player.container;
    const colors = [0x2f8f57, 0xf0a91b, 0x3a7bd8, 0xffffff, 0xd85a30];
    for (let i = 0; i < 16; i++) {
      const conf = this.add
        .rectangle(p.x + Phaser.Math.Between(-14, 14), p.y - 20, 6, 9, colors[i % colors.length])
        .setDepth(95000)
        .setAngle(Phaser.Math.Between(0, 360));
      this.tweens.add({
        targets: conf,
        y: conf.y - Phaser.Math.Between(70, 150),
        x: conf.x + Phaser.Math.Between(-60, 60),
        angle: conf.angle + Phaser.Math.Between(-200, 200),
        alpha: 0,
        duration: Phaser.Math.Between(700, 1200),
        ease: "Quad.easeOut",
        onComplete: () => conf.destroy(),
      });
    }
  }

  private buildMarker(def: InteractableDef): Phaser.GameObjects.Container {
    const parts: Phaser.GameObjects.GameObject[] = [];
    parts.push(...this.buildInteractableArt(def));
    if (!this.hideLabels) {
      parts.push(
        this.add
          .text(0, def.type === "npc" ? -66 : -40, def.label, { fontFamily: "Segoe UI, sans-serif", fontSize: "12px", color: "#241d14" })
          .setOrigin(0.5, 1),
      );
    }
    return this.add.container(0, 0, parts);
  }

  /**
   * Arte visual de un interactivo (SIN etiqueta). Por defecto son formas
   * placeholder por tipo; las escenas rediseñadas lo sobrescriben para
   * dibujar props reales. La lógica (posición, radio, depth) no depende de esto.
   */
  protected buildInteractableArt(def: InteractableDef): Phaser.GameObjects.GameObject[] {
    switch (def.type) {
      case "npc": return [P.worker(this, { shirt: def.color, ...P.personLook(def.label ?? def.ref ?? def.id) })];
      case "locker": return [P.locker(this)];
      case "machine": return [P.machine(this, def.color)];
      case "extinguisher": return [P.extinguisher(this)];
      case "sign": return [P.warningSign(this, "generic")];
      case "door": return [P.gate(this)];
      case "epi": return [P.helmet(this)];
      case "tool": return [P.fallenTool(this)];
      case "document": return [P.crate(this, 0xe0d8c0)];
      case "vehicle": return [P.crateStack(this)];
      case "firstaid": return [P.crate(this, 0xeef2ee)];
      default: return [P.crate(this, def.color)];
    }
  }

  /** Coloca un prop decorativo (no interactivo) con profundidad isométrica. */
  protected addProp(obj: Phaser.GameObjects.GameObject & { setPosition(x: number, y: number): unknown; setDepth(d: number): unknown }, wx: number, wy: number, dz = 0): void {
    const { sx, sy } = this.iso(wx, wy);
    obj.setPosition(sx, sy);
    obj.setDepth(isoDepth(wx, wy) + dz);
  }

  private repositionStatics(): void {
    for (const it of this.items) {
      const { sx, sy } = this.iso(it.def.pos.x, it.def.pos.y);
      it.root.setPosition(sx, sy);
    }
    for (const a of this.ambient) { const p = this.iso(a.wx, a.wy); a.obj.setPosition(p.sx + a.dx, p.sy + a.dy); }
    if (this.state.chapter(this.loc).done) this.onResolved();
  }

  private spawnPlayer(): void {
    const saved = SaveManager.get();
    const useSaved = saved.location === this.loc && !!saved.player;
    const x = useSaved ? Phaser.Math.Clamp(saved.player.x, 0, this.grid.w - 1) : this.spawn.x;
    const y = useSaved ? Phaser.Math.Clamp(saved.player.y, 0, this.grid.h - 1) : this.spawn.y;
    this.player = new Player(this, x, y);
    this.player.sync(this.origin);
    SaveManager.patch({ location: this.loc });
    if (this.state.chapter(this.loc).done) this.onResolved();
  }

  private setupInput(): void {
    const kb = this.input.keyboard!;
    this.keyUp = kb.addKey("W");
    this.keyDown = kb.addKey("S");
    this.keyLeft = kb.addKey("A");
    this.keyRight = kb.addKey("D");
    this.keyE = kb.addKey("E");
    kb.addKeys({ up: "UP", down: "DOWN", left: "LEFT", right: "RIGHT" });
    this.keyE.on("down", () => this.interact());
    // G: activa/desactiva el MODO AYUDA (flecha guía). Por defecto OFF (más reto).
    kb.addKey("G").on("down", () => this.toggleAyuda());
  }

  private buildPrompt(): void {
    this.promptBg = this.add.graphics();
    this.promptKey = this.add.text(0, 0, "E", { fontFamily: "Segoe UI, sans-serif", fontSize: "13px", fontStyle: "bold", color: "#ffffff" }).setOrigin(0.5);
    this.promptText = this.add.text(0, 0, "", { fontFamily: "Segoe UI, sans-serif", fontSize: "14px", color: "#241d14" }).setOrigin(0, 0.5);
    this.prompt = this.add.container(0, 0, [this.promptBg, this.promptKey, this.promptText]).setDepth(90000).setVisible(false);
    // indicador que señala el objeto interactivo más cercano
    const hg = this.add.graphics();
    hg.fillStyle(0xf0a91b, 1); hg.fillTriangle(-8, -7, 8, -7, 0, 4);
    hg.fillStyle(0xffe8b8, 0.5); hg.fillTriangle(-8, -7, 8, -7, 0, -1);
    hg.lineStyle(1.5, 0x8a5a10, 0.8); hg.strokeTriangle(-8, -7, 8, -7, 0, 4);
    this.highlight = this.add.container(0, 0, [hg]).setDepth(89000).setVisible(false);
  }

  // ----------------- interacción -----------------
  private interact(): void {
    if (this.modalOpen || !this.nearest) return;
    // pulso de respuesta sobre el objeto
    const root = this.nearest.root;
    this.tweens.add({ targets: root, scaleX: 1.1, scaleY: 1.1, yoyo: true, duration: 110, ease: "Quad.easeOut" });
    const def = this.nearest.def;
    if (def.type === "npc") Sfx.talk(); else if (def.type === "door") Sfx.click(); else Sfx.inspect();
    if (def.type === "door") { this.leaveTo("city"); return; }
    // El minijuego (extra) tiene prioridad MIENTRAS está pendiente. Si el mismo
    // objeto es además el del trabajo final (workId) y el extra ya está hecho,
    // se pasa al trabajo/resolución en vez de repetir "ya está hecho".
    const isExtraTrigger = !!this.chapter.extra && def.id === this.chapter.extra.triggerId;
    const isWork = def.id === this.chapter.workId;
    if (isExtraTrigger && !(isWork && this.state.chapter(this.loc).extraDone)) { this.doExtra(def); return; }
    if (isWork) { this.doWork(def); return; }
    if (def.type === "npc") this.doTalk(def.ref!);
    else if (def.type === "locker") this.doLocker();
    else this.doInspect(def.ref!);
  }

  private inspectHotspot(ref: string): boolean {
    const hs = this.chapter.hotspots[ref];
    const ch = this.state.chapter(this.loc);
    const first = !ch.inspected.includes(ref);
    if (first && hs) {
      ch.inspected.push(ref);
      if (hs.note) this.state.addNote(hs.note);
      if (hs.epiNote) this.state.addNote({ section: "epis", heading: hs.epiNote.heading, body: hs.epiNote.body });
      if (hs.hazardId) {
        this.hazard.discover(hs.hazardId);
        const need = this.chapter.needHazards ?? 3;
        this.ui.toast("Riesgo detectado", `${hs.label} · ${Math.min(ch.hazards.length, need)}/${need}`, "warn");
      }
      if (hs.giveItem) {
        this.inv.add(hs.giveItem);
        this.ui.toast("Recogido", itemName(hs.giveItem), "info");
        Sfx.pickup();
        this.markAttended(ref);
      }
      this.state.persist();
    }
    return first;
  }

  private doInspect(ref: string): void {
    const first = this.inspectHotspot(ref);
    const hs = this.chapter.hotspots[ref];
    const ch = this.state.chapter(this.loc);
    const extra: { hazard?: { category: string; severity: number }; item?: string } = {};
    if (first && hs?.hazardId) {
      const hz = this.chapter.hazards.find((h) => h.id === hs.hazardId);
      if (hz) extra.hazard = { category: hz.category, severity: hz.severity };
    }
    if (first && hs?.giveItem) extra.item = itemName(hs.giveItem);
    // evaluación de riesgo (identificar + controlar) — sustituye a la microdecisión
    const assess = hs?.assess && ch.scores[ref] == null ? hs.assess : undefined;
    const onAssess = (r: AssessResult): void => {
      ch.scores[ref] = r.score;
      this.security.change(this.loc, r.grade === "best" ? 4 : r.grade === "ok" ? 1 : -5);
      if (r.grade === "best") Sfx.good(); else if (r.grade === "bad") Sfx.warn(); else Sfx.click();
      this.markAttended(ref);
      if (r.grade !== "bad" && hs?.assess?.note) this.state.addNote(hs.assess.note);
      const msg = r.grade === "best" ? `Riesgo bien controlado · +seguridad · ${r.score}/5`
        : r.grade === "ok" ? `Sirve, pero se podía mejor · ${r.score}/5`
        : `Medida peligrosa · −seguridad · ${r.score}/5`;
      this.ui.toast(r.grade === "bad" ? "Cuasi-accidente" : "Evaluación de riesgo", msg, r.grade === "best" ? "good" : "warn");
      this.state.persist();
      this.updateHud();
      this.announceProgress();
    };
    // microdecisión de actuación (una vez por objeto; solo si no hay evaluación)
    const actKey = `act_${ref}`;
    const action = !assess && hs?.action && !ch.objectives[actKey] ? hs.action : undefined;
    const onChoose = (i: number): void => {
      const opt = hs?.action?.options[i];
      if (!opt) return;
      ch.objectives[actKey] = true;
      this.security.change(this.loc, opt.ok ? 3 : -3);
      if (opt.ok && hs?.action?.note) this.state.addNote(hs.action.note);
      this.markAttended(ref);
      this.ui.toast(opt.ok ? "Bien hecho" : "Mala decisión", opt.ok ? "Riesgo controlado · +seguridad" : "Eso agrava el riesgo · −seguridad", opt.ok ? "good" : "warn");
      this.state.persist();
      this.updateHud();
      this.announceProgress();
    };
    // medición con instrumento: guarda su nota la primera vez que se mide
    const measKey = `meas_${ref}`;
    const onMeasure = (): void => {
      if (ch.objectives[measKey]) return;
      ch.objectives[measKey] = true;
      if (hs?.instrument?.note) this.state.addNote(hs.instrument.note);
      Sfx.measure();
      this.state.persist();
      this.updateHud();
      this.announceProgress();
    };
    this.openModal(() => this.ui.showInspect(hs, first, extra, action, onChoose, onMeasure, assess, onAssess));
    this.updateHud();
    this.announceProgress();
  }

  private doTalk(ref: string): void {
    const npc = this.chapter.npcs[ref];
    const ch = this.state.chapter(this.loc);
    this.openModal(() =>
      this.ui.showDialogue(npc, {
        onFlag: (f) => this.state.setFlag(f),
        onNoteFromStep: (step) => {
          if (!("choices" in step) && step.note) this.state.addNote(step.note);
        },
        onEnd: () => {
          this.modalOpen = false;
          if (!ch.talked.includes(ref)) {
            ch.talked.push(ref);
            if (npc.note) this.state.addNote(npc.note);
            this.state.persist();
            const need = this.chapter.needTalk ?? 2;
            this.ui.toast("Testimonio anotado", `${npc.name} · ${Math.min(ch.talked.length, need)}/${need}`, "info");
          }
          this.updateHud();
          this.announceProgress();
        },
      }),
    );
  }

  private doLocker(): void {
    this.openModal(() =>
      this.ui.showLocker(this.chapter, () => {
        this.modalOpen = false;
        this.refreshEquipped();
        this.equipPop();
        this.updateHud();
        const missing = this.chapter.epi.needed.filter((id) => !this.equip.hasEquipment(id));
        if (this.chapter.epi.needed.length && missing.length === 0) this.ui.toast("Equipo completo", "Llevas todos los EPI necesarios", "good");
        else if (missing.length) this.ui.toast("Taquilla", `Aún te falta: ${missing.map(itemName).join(", ")}`, "warn");
        else this.ui.toast("Taquilla", "Equipo elegido", "info");
        this.announceProgress();
      }),
    );
  }

  private doExtra(def: InteractableDef): void {
    const extra = this.chapter.extra!;
    if (def.ref) this.inspectHotspot(def.ref);
    const ch = this.state.chapter(this.loc);
    if (ch.extraDone) {
      this.openModal(() => this.ui.showConsequence(extra.label, "<p>Ya está hecho. La zona está preparada para trabajar con seguridad.</p>", "Cerrar", () => {}));
      this.updateHud();
      return;
    }
    const onSuccess = () => {
      this.modalOpen = false;
      ch.extraDone = true;
      if (extra.note) this.state.addNote(extra.note);
      this.state.persist();
      this.updateHud();
      const msgs: Record<string, string> = {
        lockout: "La máquina queda consignada.",
        classify: "Almacén ordenado por familias.",
        ergo: "Puesto ajustado.",
        decision: "Actuación en marcha.",
      };
      this.ui.toast("Hecho", msgs[extra.type], "good");
      // Si este objeto es también el del trabajo final, NO lo marcamos atendido
      // aún: debe seguir resaltado y guiado hasta que se remate el trabajo.
      if (def.ref && def.id !== this.chapter.workId) this.markAttended(def.ref);
      this.announceProgress();
    };
    this.openModal(() => {
      if (extra.type === "lockout") this.ui.showLockout(extra, onSuccess);
      else if (extra.type === "classify") this.ui.showClassify(extra, onSuccess);
      else if (extra.type === "ergo") this.ui.showErgo(extra, onSuccess);
      else this.ui.showDecide(extra, onSuccess);
    });
  }

  private doWork(def: InteractableDef): void {
    const ref = def.ref;
    if (ref) this.inspectHotspot(ref);
    const hs = ref ? this.chapter.hotspots[ref] : undefined;
    const ch = this.state.chapter(this.loc);
    if (ch.done) {
      this.openModal(() => this.ui.showConsequence("Caso resuelto", "<p>Ya has dejado este sector seguro. Puedes volver a la ciudad por la salida.</p>", "Cerrar", () => {}));
      return;
    }
    const missingEpi = this.chapter.epi.needed.filter((id) => !this.equip.hasEquipment(id));
    const needExtra = !!this.chapter.extra && !ch.extraDone;
    const needTalk = Math.max(0, (this.chapter.needTalk ?? 2) - ch.talked.length);
    const needHaz = Math.max(0, (this.chapter.needHazards ?? 3) - ch.hazards.length);
    const canWork = this.mission.canReport();

    if (canWork) {
      const equipMsg = this.chapter.epi.needed.length ? "y con el equipo puesto " : "";
      const body = `${hs ? `<p>${hs.text}</p>` : ""}<div class="epi-callout" style="border-color:#2f8f57;background:#eafaf0">Todo en orden ${equipMsg}. Puedes trabajar con seguridad.</div>`;
      this.openModal(() => this.ui.showConsequence("Manos a la obra", body, "Hacer el trabajo", () => this.resolve()));
      return;
    }

    // aún no se puede: consecuencia jugable que explica qué falta
    const lines: string[] = [];
    if (needTalk > 0) lines.push(`Habla con más gente (${needTalk} más).`);
    if (needHaz > 0) lines.push(`Detecta más riesgos (${needHaz} más).`);
    if (needExtra) lines.push(`Primero: <b>${this.chapter.extra!.label}</b>.`);
    for (const id of missingEpi) lines.push(this.chapter.consequence.lines[id] ?? `Te falta el EPI: ${itemName(id)}.`);
    if (!ch.objectives["work_warned"]) {
      ch.objectives["work_warned"] = true;
      this.security.change(this.loc, -3);
      this.state.persist();
    }
    const body = `${hs ? `<p>${hs.text}</p>` : ""}<div class="epi-callout" style="border-color:#c9830a">${lines.join("<br>")}</div>`;
    this.openModal(() => this.ui.showConsequence("Todavía no", body, "Entendido", () => {}));
    this.updateHud();
  }

  private resolve(): void {
    const ch = this.state.chapter(this.loc);
    if (ch.done) return;
    if (!this.mission.markReport()) return;
    Sfx.resolve();
    const grade = this.inspectionGrade();
    // la seguridad final refleja la CALIDAD de la evaluación (consecuencias con dientes)
    const finalSec = grade.max > 0
      ? Math.round(this.chapter.secBad + (grade.pct / 100) * (this.chapter.secGreat - this.chapter.secBad))
      : this.chapter.secGreat;
    this.security.set(this.loc, finalSec);
    if (this.chapter.reward && !this.inv.has(this.chapter.reward)) this.inv.add(this.chapter.reward);
    this.chapter.resolveNotes?.forEach((n) => this.state.addNote(n));
    this.onResolved();
    this.celebrate();
    const next = DISTRICTS.find((d) => d.requires === this.loc);
    const gradeBlock = grade.max > 0
      ? `<div class="epi-callout" style="border-color:${grade.color};background:#fff7e4"><b>Nota de inspección: ${grade.rank}</b> — ${grade.got}/${grade.max} puntos (${grade.pct}%).<br>${grade.tip}</div>`
      : "";
    // resumen del caso (estadísticas)
    const hzTotal = this.chapter.hazards.filter((h) => h.discoverable).length;
    const stat = (label: string, value: string) => `<div class="epi-stat"><span>${label}</span><b>${value}</b></div>`;
    const statsBlock = `<div class="epi-stats">${stat("Riesgos detectados", `${ch.hazards.length}/${hzTotal}`)}${stat("Testimonios", `${ch.talked.length}`)}${stat("Seguridad final", `${finalSec}%`)}${grade.max > 0 ? stat("Nota inspección", `${grade.rank} · ${grade.pct}%`) : ""}</div>`;
    this.openModal(() =>
      this.ui.showConsequence("Caso resuelto", this.chapter.resolveText + statsBlock + gradeBlock, "Continuar", () => {
        this.modalOpen = false;
        this.updateHud();
        this.ui.toast("Capítulo completado", `Seguridad ${finalSec}%`, "good");
        if (grade.max > 0) this.ui.toast("Nota de inspección", `${grade.rank} · ${grade.pct}%`, grade.pct >= 75 ? "good" : "warn");
        if (next?.name) { this.ui.toast("Distrito desbloqueado", next.name, "unlock"); Sfx.unlockChime(); }
      }),
    );
    this.updateHud();
  }

  /** Nota de la inspección a partir de las evaluaciones de riesgo hechas. */
  private inspectionGrade(): { got: number; max: number; pct: number; rank: string; color: string; tip: string } {
    const ch = this.state.chapter(this.loc);
    const refs = Object.keys(this.chapter.hotspots).filter((k) => this.chapter.hotspots[k].assess);
    const max = refs.length * 5;
    const got = refs.reduce((s, k) => s + (ch.scores[k] ?? 0), 0);
    const pct = max ? Math.round((got / max) * 100) : 0;
    const rank = pct >= 90 ? "Excelente" : pct >= 75 ? "Notable" : pct >= 50 ? "Aprobado" : "Insuficiente";
    const color = pct >= 75 ? "#2f8f57" : pct >= 50 ? "#c9830a" : "#b23a2a";
    const tip = pct >= 90 ? "Identificaste y controlaste los riesgos por su medida más eficaz."
      : pct >= 50 ? "Bien, pero algún riesgo se controló con una medida de menor rango del posible."
      : "Varios riesgos quedaron mal identificados o mal controlados. Recuerda la jerarquía: primero eliminar, luego proteger a todos, y el EPI el último.";
    return { got, max, pct, rank, color, tip };
  }

  protected refreshEquipped(): void {
    const ch = this.state.chapter(this.loc);
    ch.equipped = this.chapter.epi.needed.every((id) => this.equip.hasEquipment(id));
    this.state.persist();
  }

  // ----------------- feedback de progreso -----------------
  /** Anuncia por toast los objetivos recién cumplidos y cuándo se puede resolver. */
  private announceProgress(): void {
    for (const v of this.mission.visibleObjectives()) {
      if (v.done && !this.notifiedObjectives.has(v.objective.id)) {
        this.notifiedObjectives.add(v.objective.id);
        this.ui.toast("Objetivo cumplido", v.objective.label, "good");
      }
    }
    if (!this.notifiedReady && this.mission.canReport()) {
      this.notifiedReady = true;
      const label = this.workLabel();
      this.ui.toast("Listo para resolver", label ? `Ve a: ${label}` : "Ya puedes hacer el trabajo", "good");
    }
  }

  private workLabel(): string {
    const def = this.getInteractables().find((d) => d.id === this.chapter.workId);
    return def?.label ?? "";
  }

  /** Dibuja el bocadillo de acción: tarjeta blanca redondeada + tecla E (keycap). */
  private layoutPrompt(): void {
    const padX = 12, keyW = 22, keyH = 22, gap = 9, h = 34;
    const tw = this.promptText.width;
    const totalW = padX + keyW + gap + tw + padX;
    const left = -totalW / 2;
    const g = this.promptBg;
    g.clear();
    // sombra suave
    g.fillStyle(0x1a1206, 0.26); g.fillRoundedRect(left + 1, -h / 2 + 5, totalW, h, 13);
    // tarjeta blanca con borde tenue
    g.fillStyle(0xffffff, 0.98); g.fillRoundedRect(left, -h / 2, totalW, h, 13);
    g.lineStyle(1.5, 0xdcd3c2, 1); g.strokeRoundedRect(left, -h / 2, totalW, h, 13);
    // keycap (base oscura + cara)
    const kx = left + padX;
    g.fillStyle(0x14161a, 1); g.fillRoundedRect(kx, -keyH / 2 + 2, keyW, keyH, 5);
    g.fillStyle(0x363b42, 1); g.fillRoundedRect(kx, -keyH / 2, keyW, keyH - 2, 5);
    g.lineStyle(1, 0x50565e, 0.9); g.strokeRoundedRect(kx + 1.5, -keyH / 2 + 1.5, keyW - 3, keyH - 6, 3);
    // textos
    this.promptKey.setPosition(kx + keyW / 2, -1);
    this.promptText.setPosition(kx + keyW + gap, 0);
  }

  /** Marca un interactivo como atendido: lo atenúa y le pone una etiqueta. */
  private markAttended(ref: string): void {
    const ch = this.state.chapter(this.loc);
    if (ch.objectives["attended_" + ref]) return;
    ch.objectives["attended_" + ref] = true;
    this.state.persist();
    this.applyAttended(ref, true);
  }

  private applyAttended(ref: string, live = false): void {
    const it = this.items.find((i) => i.def.ref === ref);
    const ch = this.state.chapter(this.loc);
    const hs = this.chapter.hotspots[ref];
    if (it) {
      if (hs?.giveItem || hs?.clearOnResolve) {
        // desaparece del mundo: recogido, o limpiado/apagado (queda resuelto de verdad)
        if (live) {
          this.tweens.add({ targets: it.root, alpha: 0, scaleX: 0.5, scaleY: 0.5, y: it.root.y - 14, duration: 380, ease: "Back.easeIn", onComplete: () => it.root.setVisible(false) });
        } else {
          it.root.setVisible(false);
        }
      } else {
        it.root.setAlpha(0.55);
        const isLotoMachine = it.def.type === "machine" && this.chapter.extra?.type === "lockout" && this.chapter.extra.triggerId === ref;
        const label = isLotoMachine ? "consignado" : ch.scores[ref] != null ? "resuelto" : "hecho";
        const tag = this.add
          .text(0, it.def.type === "npc" ? -70 : -46, label, {
            fontFamily: "Segoe UI, sans-serif", fontSize: "10px", color: "#eafaf0",
            backgroundColor: "#1e5b38", padding: { x: 4, y: 1 },
          })
          .setOrigin(0.5, 1);
        it.root.add(tag);
        if (it.def.type === "machine") {
          // el LED de alerta pasa a VERDE fijo: la máquina ya es segura
          const led = this.ambient.find((a) => a.wx === it.def.pos.x && a.wy === it.def.pos.y);
          if (led) { led.obj.setFillStyle(0x2f8f57).setScale(1).setAlpha(0.95); this.ambient = this.ambient.filter((a) => a !== led); }
          // consignación LOTO: candado visible sobre la máquina
          if (isLotoMachine) it.root.add(this.padlock());
        }
      }
    }
    this.attendedEffect(ref, live);
  }

  /** Icono de candado (consignación LOTO) que se pega a una máquina resuelta. */
  private padlock(): Phaser.GameObjects.Container {
    const c = this.add.container(0, -30);
    const g = this.add.graphics();
    g.fillStyle(0x1a1206, 0.18); g.fillEllipse(0, 10, 20, 5);
    g.lineStyle(3, 0xbdbdc2, 1); g.beginPath(); g.arc(0, -3, 5.5, Math.PI, 0); g.strokePath(); // arco
    g.fillStyle(0xf0c400, 1); g.fillRoundedRect(-8, -3, 16, 13, 2); // cuerpo
    g.fillStyle(0x8a6a00, 1); g.fillCircle(0, 3, 1.8); g.fillRect(-0.8, 3, 1.6, 4); // cerradura
    c.add(g);
    return c;
  }

  /** Reaplica el estado "atendido" al reconstruir la escena (spawn/resize). */
  private applyPersistedAttended(): void {
    const ch = this.state.chapter(this.loc);
    for (const it of this.items) {
      const ref = it.def.ref;
      if (ref && ch.objectives["attended_" + ref]) this.applyAttended(ref, false);
    }
  }

  /** ¿El interactivo más cercano ya está atendido? (para no resaltarlo). */
  private isAttended(def: InteractableDef): boolean {
    return !!def.ref && !!this.state.chapter(this.loc).objectives["attended_" + def.ref];
  }

  private openModal(fn: () => void): void {
    this.modalOpen = true;
    fn();
    this.time.delayedCall(50, () => this.watchModalClose());
  }

  private watchModalClose(): void {
    if (document.querySelector(".epi-backdrop")) this.time.delayedCall(120, () => this.watchModalClose());
    else this.modalOpen = false;
  }

  protected updateHud(): void {
    const total = this.chapter.hazards.filter((h) => h.discoverable).length;
    const found = this.state.chapter(this.loc).hazards.length;
    this.ui.updateHud(this.mission, this.loc, this.security.get(this.loc), { found, total });
  }

  // ----------------- loop -----------------
  update(_time: number, delta: number): void {
    const dt = delta / 1000;
    this.hlPhase += delta * 0.007;
    if (!this.modalOpen) {
      const kb = this.input.keyboard!;
      const up = this.keyUp.isDown || kb.keys[Phaser.Input.Keyboard.KeyCodes.UP]?.isDown;
      const down = this.keyDown.isDown || kb.keys[Phaser.Input.Keyboard.KeyCodes.DOWN]?.isDown;
      const left = this.keyLeft.isDown || kb.keys[Phaser.Input.Keyboard.KeyCodes.LEFT]?.isDown;
      const right = this.keyRight.isDown || kb.keys[Phaser.Input.Keyboard.KeyCodes.RIGHT]?.isDown;
      let dx = 0;
      let dy = 0;
      if (up) { dx -= 1; dy -= 1; }
      if (down) { dx += 1; dy += 1; }
      if (left) { dx -= 1; dy += 1; }
      if (right) { dx += 1; dy -= 1; }
      if (dx !== 0 || dy !== 0) {
        const px = this.player.x;
        const py = this.player.y;
        this.player.move(dx, dy, dt, this.grid);
        // colisión sólida: si el destino está bloqueado, no avanzar
        const blocked = this.isBlocked(this.player.x, this.player.y);
        if (blocked) {
          this.player.x = px;
          this.player.y = py;
        }
        // pasos de EPI (alternando pie), solo si de verdad avanza
        if (!blocked && (this.player.x !== px || this.player.y !== py)) {
          this.footT += delta;
          if (this.footT >= 290) { this.footT = 0; this.footHi = !this.footHi; Sfx.foot(this.footHi); }
        }
      } else {
        this.footT = 290; // al reanudar la marcha, el primer paso suena ya
      }
    }
    this.player.sync(this.origin);
    this.updateProximity();

    // vida ambiental: balanceo de respiración de los NPC (idle bob)
    this.idleT += delta;
    this.updateGuide();
    for (const it of this.items) {
      if (it.def.type !== "npc") continue;
      const base = this.iso(it.def.pos.x, it.def.pos.y);
      it.root.y = base.sy + Math.sin(this.idleT * 0.0025 + it.def.pos.x + it.def.pos.y) * 2;
    }
    // LEDs parpadeantes de las máquinas
    for (const a of this.ambient) {
      if (!a.obj.active) continue;
      const t = 0.5 + 0.5 * Math.sin(this.idleT * a.sp + a.ph);
      a.obj.setScale(0.65 + t * 0.7).setAlpha(0.35 + t * 0.65);
    }
    // efectos: balanceo de lámparas + pulso de focos (autolimpiante)
    if (this.fx.some((f) => !f.obj.active)) this.fx = this.fx.filter((f) => f.obj.active);
    for (const f of this.fx) {
      if (f.kind === "rise") {
        const cyc = (((this.idleT * f.sp + f.ph) % 1) + 1) % 1; // 0..1
        f.obj.y = (f.by ?? 0) - cyc * (f.rise ?? 20);
        f.obj.setAlpha?.(f.amp * (1 - cyc));
        f.obj.setScale?.(0.5 + cyc * (f.grow ?? 1));
        continue;
      }
      const w = Math.sin(this.idleT * f.sp + f.ph);
      if (f.kind === "sway") f.obj.setAngle?.(w * f.amp);
      else f.obj.setAlpha?.(Math.max(0, f.base + w * f.amp));
    }

    this.saveAccum += delta;
    if (this.saveAccum > 1000) {
      this.saveAccum = 0;
      SaveManager.patch({ player: { x: this.player.x, y: this.player.y }, location: this.loc });
    }
  }

  private updateProximity(): void {
    let best: SceneInteractable | null = null;
    let bestD = Infinity;
    for (const it of this.items) {
      const d = Math.hypot(it.def.pos.x - this.player.x, it.def.pos.y - this.player.y);
      const r = it.def.radius ?? 1.2;
      if (d <= r && d < bestD) { best = it; bestD = d; }
    }
    this.nearest = best;
    if (best && !this.modalOpen) {
      this.promptText.setText(`${verbLabel(best.def.verb)} ${best.def.label}`);
      this.layoutPrompt();
      this.prompt.setPosition(this.player.container.x, this.player.container.y - 96).setVisible(true);
      const bob = Math.sin(this.hlPhase) * 3;
      // no resaltar lo que ya está atendido: guía hacia lo que falta
      this.highlight.setPosition(best.root.x, best.root.y - 80 + bob).setVisible(!this.isAttended(best.def));
    } else {
      this.prompt.setVisible(false);
      this.highlight.setVisible(false);
    }
  }
}

function verbLabel(verb?: string): string {
  switch (verb) {
    case "talk": return "Hablar";
    case "open": return "Abrir";
    case "read": return "Leer";
    case "use": return "Usar";
    case "pickup": return "Recoger";
    default: return "Inspeccionar";
  }
}
